import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Avatar from '../Avatar';
import Rating from '../Rating';
import { monthDayYearWithSlashFormat } from '../../../../constants/date';
import { appPath } from '../../../../constants';
import UsersService from '../../../../services/UsersService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapPin, faHeart, faPhone, faEnvelope, faBan, faComment, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { colors } from '../../../../constants/ui';
import ConversationContainer from '../../../app/messages/ConversationContainer';
import { startSupportConversation, startConversation } from '../../../../actions/conversations';
import { createCompanyWorker, updateCompanyWorker, deleteCompanyWorker, getCompanyFavAndBlocks } from '../../../../actions/companies';
import { favStatuses } from '../../../../constants/workers';
import { directConversationsSubTypes } from '../../../../constants/conversations';

class WorkerQuickView extends Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
    languages: PropTypes.array.isRequired,
    favAndBlocks: PropTypes.array.isRequired,
    startSupportConversation: PropTypes.func.isRequired,
    startConversation: PropTypes.func.isRequired,
    createCompanyWorker: PropTypes.func.isRequired,
    updateCompanyWorker: PropTypes.func.isRequired,
    deleteCompanyWorker: PropTypes.func.isRequired,
    getCompanyFavAndBlocks: PropTypes.func.isRequired,
    updateConversationMessagesCount: PropTypes.func.isRequired
  };

  initialState = {
    fav_status: this.props.fav_status,
    favBlockLoading: false,
    showChat: false,
    conversation_id: null,
    conversationLoading: false
  };

  state = this.initialState;

  isAdmin = UsersService.isAdmin();

  headingClassName = 'text-md text-left mb-2';

  showChat = () => {
    this.setState({ conversationLoading: true });

    if (this.isAdmin) {
      this.props.startSupportConversation(this.props.id)
        .then(this.openConversation);
    } else {
      this.props.startConversation({
        type: directConversationsSubTypes.private,
        users: [this.props.id, this.props.user.userid] // [workerId, employerId]
      }).then(this.openConversation);
    }
  };

  openConversation = ({ conversation_id }) => {
    this.setState({
      conversation_id,
      showChat: true,
      conversationLoading: false
    });
  };

  hideChat = () => this.setState({ showChat: false, conversation_id: null });

  handleChat = () => this.state.showChat ? this.hideChat() : this.showChat();

  startFavBlockLoading = () => this.setState({ favBlockLoading: true });

  finishFavBlockLoading = () => this.setState({ favBlockLoading: false });

  setWorkerFavStatus = fav_status => this.setState(prevState => ({
    fav_status: fav_status === prevState.fav_status ? null : fav_status
  }));

  handleCompanyWorker = status => {
    this.startFavBlockLoading();

    const { favAndBlocks, createCompanyWorker, deleteCompanyWorker, updateCompanyWorker, id } = this.props;
    const { fav_status } = this.state;

    const myWorker = favAndBlocks.find(worker => worker.worker_id === id);

    let handleWorkerFavStatus;

    if (!myWorker) {
      handleWorkerFavStatus = () => createCompanyWorker({ worker_id: id, status });
    } else if (fav_status === status) {
      handleWorkerFavStatus = () => deleteCompanyWorker(myWorker.id);
    } else if (fav_status !== status) {
      handleWorkerFavStatus = () => updateCompanyWorker(myWorker.id, { status });
    }

    return handleWorkerFavStatus()
      .then(() => this.setWorkerFavStatus(status))
      .then(this.finishFavBlockLoading);
  };

  _renderSkills = () => {
    const { skills } = this.props;

    if (skills && skills.length) {
      return (
        <div className='worker-details-row'>
          <div className={this.headingClassName}>Worker skills:</div>

          <div className='row d-flex flex-wrap align-items-start'>
            {skills.map(skill => (
              <div className='worker-details-skill col-4 col-sm-3' key={skill.skill_label}>
                <Avatar photo_image_url={skill.icon_url} className='mx-auto mb-1' />
                <div className='lh-middle'>
                  <em className='text-sm'>{skill.skill_label}</em>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  _renderJobs = () => {
    const { jobs } = this.props;

    if (jobs && jobs.length) {
      return jobs.map(job => (
        <div className='job-card-worker align-items-center d-flex text-left' key={job.id}>
          <Avatar photo_image_url={job.icon_url} />

          <div className='d-flex align-items-center w-100 justify-content-between'>
            <div>
              <strong>{job.skill_label}</strong>
              <div>{job.location_name || job.address}</div>
            </div>

            <strong>{moment(job.start_date).format(monthDayYearWithSlashFormat)}</strong>
          </div>
        </div>
      ));
    }

    return <div className='text-muted text-left' children='Worker has no completed jobs yet.' />;
  };

  _renderLanguages = () => {
    const { language_id, secondary_language_id, languages } = this.props;

    if (!language_id) {
      return;
    }

    const primaryLang = languages.find(lang => lang.id === language_id);
    const secondaryLang = languages.find(lang => lang.id === secondary_language_id);

    return (
      <div className='worker-details-row'>
        <div className={this.headingClassName}>Primary language: {primaryLang.language_name}</div>

        {secondaryLang && (
          <div className={this.headingClassName}>Secondary language: {secondaryLang.language_name}</div>
        )}
      </div>
    );
  };

  _renderFullName = () => {
    const { first_name, last_name, id } = this.props;

    const fullName = `${first_name} ${last_name}`;

    return (
      <div className='text-md mb-1 text-bold'>
        {this.isAdmin ? (
          <Link to={`/${appPath}/workers/${id}`} className='button-link'>
            {fullName}
          </Link>
        ) : fullName}
      </div>
    );
  };

  _renderActions = () => {
    const { phone, email } = this.props;
    const Render = () => {
      if (this.isAdmin && (email || phone)) {
        return (
          <div className='mr-auto'>
            {phone && (
              <div className='d-flex mb-3'>
                <a href={`tel:${phone}`} className='d-inline-flex align-items-center button-link'>
                  <FontAwesomeIcon icon={faPhone} color={colors.mint} />
                  <div className='ml-2'>{phone}</div>
                </a>
              </div>
            )}

            {email && (
              <div className='d-flex'>
                <a href={`mailto:${email}`} className='d-inline-flex align-items-center button-link'>
                  <FontAwesomeIcon icon={faEnvelope} color={colors.mint} />
                  <div className='ml-2 text-lowercase'>{email}</div>
                </a>
              </div>
            )}
          </div>
        );
      }

      return (
        <Fragment>
          <button
            onClick={() => this.handleCompanyWorker(favStatuses.favorite)}
            disabled={this.state.favBlockLoading}
            className={`button button-rounded ${this.state.fav_status === favStatuses.favorite ? 'button-delete' : 'button-cancel'} mr-3`}
          >
            <FontAwesomeIcon icon={faHeart} />
          </button>

          <button
            onClick={() => this.handleCompanyWorker(favStatuses.blocked)}
            disabled={this.state.favBlockLoading}
            className={`button button-rounded ${this.state.fav_status === favStatuses.blocked ? 'button-black' : 'button-cancel'} mr-3`}
          >
            <FontAwesomeIcon icon={faBan} />
          </button>
        </Fragment>
      );
    };

    return (
      <div className='worker-details-row d-flex align-items-center justify-content-center'>
        <Render />

        <button
          disabled={this.state.conversationLoading}
          onClick={this.handleChat}
          className={`button button-rounded button-primary`}
        >
          <FontAwesomeIcon icon={this.state.showChat ? faTimesCircle : faComment} />
        </button>
      </div>
    );
  };

  _renderChat = () => {
    const { showChat, conversation_id } = this.state;

    if (showChat && conversation_id) {
      return (
        <ConversationContainer
          conversationId={conversation_id}
          updateConversationMessagesCount={this.props.updateConversationMessagesCount}
        />
      );
    }

    return null;
  };

  componentDidMount () {
    if (UsersService.isEmployer()) {
      this.props.getCompanyFavAndBlocks();
    }
  }

  render () {
    const { photo_image_url, average_rating, address, total_jobs_worked, dropped_jobs } = this.props;

    return (
      <div className='worker-quick-view worker-details text-center text-capitalize'>
        <Avatar className='avatar-lg avatar-border avatar-border-primary mx-auto mb-2' photo_image_url={photo_image_url} />

        {this._renderFullName()}

        <div className='text-md mb-2'>
          <span>Worked:</span>
          <strong className='text-primary ml-1'>{total_jobs_worked}</strong>

          <span className='ml-3'>Dropped:</span>
          <strong className='text-delete ml-1'>{dropped_jobs}</strong>
        </div>

        {average_rating && (
          <div className='d-flex justify-content-center mb-2'>
            <Rating value={average_rating} disabled />
          </div>
        )}

        <div className='d-flex align-items-center justify-content-center'>
          <FontAwesomeIcon icon={faMapPin} color={colors.mint} />
          <div className='ml-2'>{address}</div>
        </div>

        {this._renderActions()}

        {this._renderChat()}

        {this._renderLanguages()}

        {this._renderSkills()}

        <div className='worker-details-row mt-0'>
          <div className={this.headingClassName}>Completed jobs:</div>

          {this._renderJobs()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
  ...state.worker.workerQuickView,
  languages: state.languagesList,
  favAndBlocks: state.companies.companyDetails.favAndBlocks
});

const mapDispatchToProps = {
  startSupportConversation,
  startConversation,
  createCompanyWorker,
  updateCompanyWorker,
  deleteCompanyWorker,
  getCompanyFavAndBlocks
};

export default connect(mapStateToProps, mapDispatchToProps)(WorkerQuickView);



// WEBPACK FOOTER //
// ./src/components/common/ui/QuickView/WorkerQuickView.js